import { ApiService } from 'src/app/modules/core/services/api.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { tap, catchError, finalize, switchMap } from 'rxjs/operators';

export interface AuthTokens {
	accessToken: string;
	refreshToken: string;
}

export interface LoginResponse {
	statusCode: number;
	error: any;
	data: {
		userId: string;
		name: string;
		accessToken: string;
		refreshToken: string;
	};
	count: any;
}

export interface RefreshResponse {
	statusCode: number;
	error: any;
	data: AuthTokens;
	count: any;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly ACCESS_TOKEN_KEY = 'access_token';
	private readonly REFRESH_TOKEN_KEY = 'refresh_token';

	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(private readonly apiService: ApiService) {}

	/**
	 * Check if user is logged in
	 */
	isLogged(): boolean {
		return !!this.getAccessToken();
	}

	/**
	 * Get stored access token
	 */
	get accessToken(): string | null {
		return this.getAccessToken();
	}

	/**
	 * Get stored refresh token
	 */
	get refreshToken(): string | null {
		return this.getRefreshToken();
	}

	/**
	 * Check if refresh token process is in progress
	 */
	get isRefreshTokenInProgress(): boolean {
		return this.isRefreshing;
	}

	/**
	 * Get refresh token subject observable
	 */
	get refreshTokenSubject$(): Observable<string | null> {
		return this.refreshTokenSubject.asObservable();
	}

	/**
	 * Login user and store tokens
	 */
	login(credentials: { email: string; password: string }): Observable<LoginResponse> {
		return this.apiService.login(credentials).pipe(
			tap((response) => {
				if (response && (response.statusCode === 200 || response.statusCode === 201) && response.data) {
					const tokens: AuthTokens = {
						accessToken: response.data.accessToken,
						refreshToken: response.data.refreshToken,
					};
					this.storeTokens(tokens);
				}
			}),
		);
	}

	/**
	 * Refresh access token using refresh token
	 */
	refreshAccessToken(): Observable<string | null> {
		const refreshToken = this.getRefreshToken();

		if (!refreshToken) {
			console.error('No refresh token available');
			this.logout();
			return throwError(() => new Error('No refresh token available'));
		}

		if (this.isRefreshing) {
			return this.refreshTokenSubject.asObservable();
		}

		this.isRefreshing = true;
		this.refreshTokenSubject.next(null);

		return this.apiService.refreshToken(refreshToken).pipe(
			switchMap((response) => {
				if (response && (response.statusCode === 200 || response.statusCode === 201) && response.data) {
					this.storeTokens(response.data);
					this.refreshTokenSubject.next(response.data.accessToken);
					return of(response.data.accessToken);
				} else {
					throw new Error('Invalid refresh response');
				}
			}),
			catchError((error) => {
				console.error('Refresh token failed:', error);
				this.logout();
				return throwError(() => error);
			}),
			finalize(() => {
				this.isRefreshing = false;
			}),
		);
	}

	/**
	 * Logout user and clear all tokens
	 */
	logout(): void {
		this.clearTokens();
		this.refreshTokenSubject.next(null);
		this.isRefreshing = false;
	}

	/**
	 * Store both access and refresh tokens
	 */
	private storeTokens(tokens: AuthTokens): void {
		localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
		localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);

		// Also store legacy session key for backward compatibility
		localStorage.setItem('session', tokens.accessToken);
	}

	/**
	 * Get access token from storage
	 */
	private getAccessToken(): string | null {
		return localStorage.getItem(this.ACCESS_TOKEN_KEY) || localStorage.getItem('session');
	}

	/**
	 * Get refresh token from storage
	 */
	private getRefreshToken(): string | null {
		return localStorage.getItem(this.REFRESH_TOKEN_KEY);
	}

	/**
	 * Clear all stored tokens
	 */
	private clearTokens(): void {
		localStorage.removeItem(this.ACCESS_TOKEN_KEY);
		localStorage.removeItem(this.REFRESH_TOKEN_KEY);
		localStorage.removeItem('session'); // Legacy compatibility
		localStorage.clear(); // Clear all localStorage for security
	}

	/**
	 * Check if tokens are available
	 */
	hasValidTokens(): boolean {
		return !!(this.getAccessToken() && this.getRefreshToken());
	}
}
